import { useEffect, useRef, useState } from "react";
import { DEMO_CHANNEL } from "../lib/demo/channel.ts";
import { fetchMetadata } from "../lib/archive/metadata.ts";
import { buildItem } from "../lib/archive/buildItem.ts";
import type { Item } from "../types.ts";

/**
 * EmptyState — resolves the demo channel identifiers to real items and
 * hands them to the parent so the queue renders the demo. Silently drops
 * any identifier that fails to resolve (FR-027 robustness).
 */
export interface EmptyStateProps {
  onLoaded: (items: Item[]) => void;
}

export function EmptyState({ onLoaded }: EmptyStateProps) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      const results = await Promise.all(
        DEMO_CHANNEL.identifiers.map(async (id) => {
          try {
            const meta = await fetchMetadata(id);
            const item = buildItem(meta);
            if (item.isEmbedFallback) return null;
            return item;
          } catch {
            return null;
          }
        }),
      );
      const items = results.filter((x): x is Item => x !== null);
      if (items.length > 0) {
        setStatus("ready");
        onLoaded(items);
      } else {
        setStatus("error");
      }
    })();
  }, [onLoaded]);

  if (status === "loading") {
    return <p className="notice">Loading the demo channel…</p>;
  }
  if (status === "error") {
    return (
      <p className="notice warning">
        Couldn&apos;t reach the demo channel. Paste an archive.org URL to get started.
      </p>
    );
  }
  return null;
}
